import { useState } from 'react'

import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { LinkRemove, Pencil } from '@factorialco/f0-react/icons/app'
import {
  ContributorDataCollectionItemFragment,
  ContributorsDataCollectionDocument,
} from 'generated/resources/graphql'
import { compact } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

import EmployeeSidepanel from 'modules/project_management/components/EmployeeSidepanel'
import { SelectedEmployeeInfo } from 'modules/project_management/components/tables/JobLevelEmployeesDataCollection/types'
import { getPeopleCount } from 'modules/project_management/lib/constants'
import { costRateCell } from 'modules/project_management/lib/utils'

const ContributorsDataCollection = ({ canManageRates }: { canManageRates: boolean }) => {
  const {
    access: { locale },
  } = useCurrentSession()
  const { navigation } = useNavigation()
  const [showEmployeeSidepanel, setShowEmployeeSidepanel] = useState(false)
  const [selectedEmployeeInfo, setSelectedEmployeeInfo] = useState<SelectedEmployeeInfo>({
    avatar: '',
    id: '0',
    firstName: '',
    lastName: '',
    billingRateCents: 0,
    costRate: 0,
    markupPercentageCents: 0,
    currency: '',
  })

  const source = useGraphqlDataCollectionSource({
    document: ContributorsDataCollectionDocument,
    totalItemSummary: (totalItems) => getPeopleCount(totalItems),
    mapData: (data) => data.projectManagement.projectContributorsConnection.nodes,
    mapVariables: ({ search, sortings }) => ({
      employeeName: search,
      sortOrder: sortings?.[0],
    }),
    mapPagination: (data) => ({
      total: data.projectManagement.projectContributorsConnection.totalCount ?? 0,
    }),
    itemActions: (item) => {
      if (!canManageRates) return
      if (item.id !== null) {
        return [
          {
            label: i18n.t('project_management.people_and_rates.sidepanel.title.edit_rates'),
            icon: Pencil,
            onClick: () => {
              track(trackingEvents.projectManagement.jobsAndRates.clickOnEditEmployeeRate, {
                origin: 'from_people_table',
              })
              setSelectedEmployeeInfo({
                avatar: item.employee?.access?.avatar ?? '',
                id: item.employee?.id ?? '0',
                firstName: item.employee?.firstName ?? '',
                lastName: item.employee?.lastName ?? '',
                billingRateCents: item.rates[0]?.billingRateCents ?? 0,
                costRate: item.rates[0]?.chargingCostCents ?? 0,
                markupPercentageCents: item.rates[0]?.markupPercentageCents ?? 0,
                currency: item.rates[0]?.currency ?? '',
              })
              setShowEmployeeSidepanel(true)
            },
          },
        ]
      }
    },
    itemUrl: (item) => {
      if (item.employee?.id) {
        return navigation.projects.contributors.contributorId.fullPath({
          employeeId: item.employee.id,
        })
      }
      return undefined
    },
    search: {
      enabled: true,
    },
    sortings: {
      fullName: {
        label: i18n.t('project_management.table.contributors_list.headers.full_name'),
      },
    },
    defaultSortings: {
      field: 'fullName',
      order: 'asc',
    },
  })

  return (
    <>
      <OneDataCollection
        emptyStates={{
          'no-data': {
            title: i18n.t(
              'project_management.table.contributors_list.no_contributors_message.title'
            ),
          },
        }}
        source={source}
        visualizations={[
          {
            type: 'table',
            options: {
              columns: compact([
                {
                  label: i18n.t('project_management.table.contributors_list.headers.full_name'),
                  render: (item: ContributorDataCollectionItemFragment) => ({
                    type: 'person',
                    value: {
                      firstName: item.employee?.firstName ?? '',
                      lastName: item.employee?.lastName ?? '',
                      src: buildUrl(item.employee?.access?.avatar),
                    },
                  }),
                  sorting: 'fullName' as const,
                },
                {
                  label: i18n.t('project_management.table.contributors_list.headers.role'),
                  render: (item: ContributorDataCollectionItemFragment) => ({
                    type: 'text',
                    value:
                      item.employee?.referenceContract?.jobCatalogTreeLevel?.ancestor?.name ?? '-',
                  }),
                },
                {
                  label: i18n.t('project_management.table.contributors_list.headers.level'),
                  render: (item: ContributorDataCollectionItemFragment) => ({
                    type: 'text',
                    value: item.employee?.referenceContract?.jobCatalogTreeLevel?.name ?? '-',
                  }),
                },
                canManageRates
                  ? {
                      label: i18n.t(
                        'project_management.job_level_employees.table.headers.cost_rate.label'
                      ),
                      info: i18n.t(
                        'project_management.job_level_employees.table.headers.cost_rate.tooltip_message'
                      ),
                      align: 'right',
                      render: ({
                        rates,
                        defaultRateCost,
                        employee,
                      }: ContributorDataCollectionItemFragment) => {
                        const defaultCost = defaultRateCost?.chargingCostCents
                        const defaultCurrency = employee?.legalEntity?.currency

                        const rate = rates[0]
                        const defaultRate =
                          defaultCost && defaultCurrency
                            ? {
                                chargingCostCents: defaultCost,
                                currency: defaultCurrency,
                              }
                            : undefined

                        return costRateCell(rate, defaultRate, locale)
                      },
                    }
                  : undefined,
                canManageRates
                  ? {
                      label: i18n.t(
                        'project_management.job_level_employees.table.headers.bill_rate.label'
                      ),
                      info: i18n.t(
                        'project_management.job_level_employees.table.headers.bill_rate.tooltip_message'
                      ),
                      align: 'right',
                      render: ({ rates }: ContributorDataCollectionItemFragment) => {
                        if (rates.length === 0) return
                        return {
                          type: 'text',
                          value: MoneyWithCurrency({
                            amount: rates[0]?.billingRateCents ?? 0,
                            currency: rates[0]?.currency ?? '',
                            optionalDecimals: true,
                            locale,
                          }),
                        }
                      },
                    }
                  : undefined,
                canManageRates
                  ? {
                      label: i18n.t(
                        'project_management.job_level_employees.table.headers.markup.label'
                      ),
                      info: i18n.t(
                        'project_management.job_level_employees.table.headers.markup.tooltip_message'
                      ),
                      align: 'right',
                      render: ({ rates }: ContributorDataCollectionItemFragment) => {
                        if (rates.length === 0) return
                        return {
                          type: 'text',
                          value: `${(rates[0]?.markupPercentageCents ?? 0) / 100}%`,
                        }
                      },
                    }
                  : undefined,
                canManageRates
                  ? {
                      label: i18n.t(
                        'project_management.job_level_employees.table.headers.margin.label'
                      ),
                      info: i18n.t(
                        'project_management.job_level_employees.table.headers.margin.tooltip_message'
                      ),
                      align: 'right',
                      render: ({ rates }: ContributorDataCollectionItemFragment) => {
                        if (rates.length === 0) return
                        return {
                          type: 'text',
                          value: `${((rates[0]?.marginPercentageCents ?? 0) / 100).toFixed(2)}%`,
                        }
                      },
                    }
                  : undefined,
                canManageRates
                  ? {
                      label: i18n.t(
                        'project_management.job_level_employees.table.headers.custom_rate.label'
                      ),
                      info: i18n.t(
                        'project_management.job_level_employees.table.headers.custom_rate.tooltip_message'
                      ),
                      render: ({ rates }: ContributorDataCollectionItemFragment) => {
                        if (rates.length === 0) return
                        if (rates[0]?.unlinked)
                          return {
                            type: 'tag',
                            value: {
                              label: i18n.t('project_management.job_level_employees.table.custom'),
                              icon: LinkRemove,
                            },
                          }
                      },
                    }
                  : undefined,
                {
                  label: i18n.t(
                    'project_management.table.contributors_list.headers.projects_assigned'
                  ),
                  align: 'right',
                  render: (item: ContributorDataCollectionItemFragment) => ({
                    type: 'number',
                    value: item.projectWorkersConnection.totalCount,
                  }),
                },
              ]),
            },
          },
        ]}
      />
      {showEmployeeSidepanel && (
        <EmployeeSidepanel
          refetchQueries={[ContributorsDataCollectionDocument]}
          showSidePanel={showEmployeeSidepanel}
          onClose={() => setShowEmployeeSidepanel(false)}
          selectedEmployeeInfo={selectedEmployeeInfo}
        />
      )}
    </>
  )
}

export default ContributorsDataCollection
